/**
 * One search box across services, food, pharmacy and adoption.
 *
 * Results are grouped rather than merged: "adoption" and "dog food" are
 * different intentions sharing an input, so each gets a section and the searcher
 * picks the shape of answer they meant. One card component renders all four —
 * the only difference between a vet and a bag of kibble is where tapping goes,
 * and the server already says that in `target`.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, MapPin, Search as SearchIcon, SearchX, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { AuthImage } from '@/components/auth-image';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { use_geolocation } from '@/lib/use-geolocation';
import { use_search } from '@/features/search/hooks';
import { route_for, type SearchGroupType, type SearchHit } from '@/features/search/types';
import { SpeciesFilterChips } from '@/components/species-chips';
import { SPECIES_OPTIONS, type PetSpecies } from '@/features/pet/types';

const FILTERS: { value: SearchGroupType | ''; label: string }[] = [
  { value: '', label: 'Everything' },
  { value: 'SERVICES', label: 'Services' },
  { value: 'FOOD', label: 'Pet food' },
  { value: 'PHARMACY', label: 'Pharmacy' },
  { value: 'ADOPTION', label: 'Adoption' },
];

/** Suggestions that exercise what the old search could not do. */
const SUGGESTIONS = ['grooming', 'dry food', 'vet', 'dog', 'vaccination'];

function HitCard({ hit }: { hit: SearchHit }) {
  return (
    <Link
      to={route_for(hit)}
      data-testid={`search-hit-${hit.id}`}
      className="flex items-center gap-3 rounded-card border-2 border-brand-outline-variant/15 bg-card p-3 transition-colors hover:border-primary/40"
    >
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-input bg-secondary/40">
        {hit.image ? (
          <AuthImage path={hit.image.thumb_path} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xl">🐾</div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate font-medium leading-snug">{hit.title}</p>
        {hit.subtitle && <p className="truncate text-xs text-muted-foreground">{hit.subtitle}</p>}
        {/* Why this matched, when the name alone doesn't say. */}
        {hit.matched_on && (
          <p className="truncate text-xs font-semibold text-primary" data-testid={`search-why-${hit.id}`}>
            {hit.matched_on}
          </p>
        )}
        {hit.rating != null && hit.review_count != null && hit.review_count > 0 && (
          <p className="flex items-center gap-0.5 text-xs text-muted-foreground">
            <Star className="h-3 w-3 fill-current text-amber-500" />
            {hit.rating.toFixed(1)}
            <span className="text-muted-foreground/70">({hit.review_count})</span>
          </p>
        )}
        {/* The server already ordered and worded these; two fit a phone row. */}
        {hit.badges.length > 0 && (
          <div className="mt-1 flex gap-1 overflow-hidden" data-testid={`search-badges-${hit.id}`}>
            {hit.badges.slice(0, 2).map((badge) => (
              <span
                key={badge}
                className="shrink-0 truncate rounded-full bg-secondary/60 px-1.5 py-0.5 text-[10px] font-medium text-secondary-foreground"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 text-right">
        {hit.price_inr && <p className="font-heading font-bold tabular-nums">₹{hit.price_inr}</p>}
        {hit.mrp_inr && (
          <p className="text-xs tabular-nums text-muted-foreground">
            <span className="line-through">₹{hit.mrp_inr}</span>
            {hit.discount_pct != null && <span className="ml-1 font-semibold text-emerald-600">{hit.discount_pct}% off</span>}
          </p>
        )}
        {hit.distance_km != null && (
          <p className="flex items-center justify-end gap-0.5 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3" /> {hit.distance_km.toFixed(1)} km
          </p>
        )}
      </div>
    </Link>
  );
}

export function SearchPage() {
  const navigate = useNavigate();
  const [params, set_params] = useSearchParams();
  // The query lives in the URL so a search can be shared, and so going back
  // from a result returns to the results rather than an empty box.
  const [q, set_q] = useState(params.get('q') ?? '');
  const [group, set_group] = useState<SearchGroupType | ''>(() => {
    // Only a known group: an unknown ?group= would be sent verbatim and the server's 422
    // would replace the results with an error.
    const g = params.get('group');
    return FILTERS.some((f) => f.value && f.value === g) ? (g as SearchGroupType) : '';
  });
  // Client change, 9 Oct 2026: the pet chip — Bird hides Dog food, keeps all-pets items.
  const [species, set_species] = useState<PetSpecies | ''>(() => {
    const s = params.get('species');
    return SPECIES_OPTIONS.some((o) => o.value === s) ? (s as PetSpecies) : '';
  });
  const [debounced, set_debounced] = useState(q);
  const geo = use_geolocation();

  useEffect(() => {
    const t = setTimeout(() => set_debounced(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debounced.trim()) next.set('q', debounced.trim());
    if (group) next.set('group', group);
    if (species) next.set('species', species);
    set_params(next, { replace: true });
  }, [debounced, group, species, set_params]);

  const { data, isFetching, error } = use_search(debounced, {
    lat: geo.lat,
    lng: geo.lng,
    group: group || undefined,
    species: species || undefined,
    // A single-group view is the "see all" screen, so it shows a real page.
    limit: group ? 30 : 4,
  });

  const typed_enough = debounced.trim().length >= 2;
  const groups = data?.groups ?? [];
  const nothing_found = typed_enough && data && data.total === 0;

  return (
    <div className="flex flex-col gap-4">
      <SectionHeader
        title="Search"
        description="Vets, walkers, food, medicine and pets looking for a home — all in one place."
      />

      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={q}
          onChange={(e) => set_q(e.target.value)}
          placeholder="Try “grooming”, “dry food”, “vaccination”…"
          className="pl-9"
          data-testid="search-input"
          aria-label="Search"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="search-filters">
        {FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            onClick={() => set_group(f.value)}
            data-testid={`search-filter-${f.value || 'ALL'}`}
            data-active={group === f.value}
            className={cn(
              'rounded-full border-2 px-3 py-1 text-xs font-semibold transition-colors',
              // Solid tokens: bg-primary/10 compiled to nothing here, so the selected chip
              // looked almost identical to the rest (client: "filter is not working").
              group === f.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <SpeciesFilterChips value={species} onChange={set_species} testid="search-species" />

      {/* Before anything is typed: show what this box can do, rather than a void. */}
      {!typed_enough && (
        <div className="flex flex-col gap-3" data-testid="search-idle">
          <p className="text-sm text-muted-foreground">Popular searches</p>
          <div className="flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => set_q(s)}
                data-testid={`search-suggestion-${s.replace(/\s+/g, '-')}`}
                className="rounded-full bg-secondary px-3 py-1.5 text-sm font-medium hover:bg-secondary/70"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && <ErrorState description={form_error_message(error, 'Could not search just now.')} />}

      {nothing_found && (
        <EmptyState
          icon={SearchX}
          title={`Nothing matches “${debounced.trim()}”`}
          description="Try a shorter word, or a category like “dry food” or “grooming”."
          data-testid="search-empty"
        />
      )}

      {typed_enough &&
        groups
          // Empty groups come back deliberately (so sections don't jump around
          // server-side), but there is nothing to draw for them here.
          .filter((g) => g.total > 0)
          .map((g) => (
            <section key={g.type} className="flex flex-col gap-2" data-testid={`search-group-${g.type}`}>
              <div className="flex items-baseline justify-between gap-3">
                <h2 className="font-heading text-base font-bold">
                  {g.label}{' '}
                  <span className="text-sm font-normal text-muted-foreground" data-testid={`search-total-${g.type}`}>
                    ({g.total})
                  </span>
                </h2>
                {/* Only worth offering when there is genuinely more to see. */}
                {!group && g.total > g.items.length && (
                  <button
                    type="button"
                    onClick={() => set_group(g.type)}
                    data-testid={`search-see-all-${g.type}`}
                    className="text-sm font-semibold text-primary hover:underline"
                  >
                    See all {g.total}
                  </button>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {g.items.map((hit) => (
                  <HitCard key={`${hit.type}-${hit.id}`} hit={hit} />
                ))}
              </div>
            </section>
          ))}

      {/* Location is optional, but the difference it makes is worth asking for. */}
      {typed_enough && geo.status === 'idle' && (
        <button
          type="button"
          onClick={geo.request}
          data-testid="search-use-location"
          className="self-start text-sm font-semibold text-primary hover:underline"
        >
          Use my location to sort by distance
        </button>
      )}

      {typed_enough && data && data.total > 0 && (
        <button
          type="button"
          onClick={() => navigate('/app/discover')}
          className="self-start text-sm text-muted-foreground hover:underline"
        >
          Browse everything instead
        </button>
      )}
    </div>
  );
}
